import { useEffect, useState, useCallback } from 'react';
import { Mail, MessageCircle, Inbox, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { ComposeMessageDialog } from './ComposeMessageDialog';

type Msg = {
  id: string;
  channel: 'sms' | 'email';
  direction: 'in' | 'out';
  subject: string | null;
  body_text: string | null;
  from_addr: string;
  to_addr: string;
  status: string;
  error: string | null;
  sent_at: string | null;
  received_at: string | null;
  read_at: string | null;
  created_at: string;
};

function fmtTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface Props {
  dealId: string;
  dealershipId: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
}

export function ConversationThreadCard({ dealId, dealershipId, customerName, customerEmail, customerPhone }: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [loading, setLoading] = useState(true);
  const [composeChannel, setComposeChannel] = useState<'email' | 'sms' | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('messages')
      .select('id, channel, direction, subject, body_text, from_addr, to_addr, status, error, sent_at, received_at, read_at, created_at')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: true });
    setMessages((data as Msg[]) ?? []);
    setLoading(false);

    // Mark unread inbound messages as read
    const unread = (data as Msg[] | null)?.filter(m => m.direction === 'in' && !m.read_at).map(m => m.id) ?? [];
    if (unread.length > 0) {
      await supabase.from('messages').update({ read_at: new Date().toISOString() }).in('id', unread);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  // Realtime subscription for this deal's messages
  useEffect(() => {
    const channel = supabase
      .channel(`messages-deal-${dealId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `deal_id=eq.${dealId}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [dealId, load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">Conversation</CardTitle>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setComposeChannel('email')}
            disabled={!customerEmail}
            title={!customerEmail ? 'No email on file' : 'Email customer'}
          >
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setComposeChannel('sms')}
            disabled={!customerPhone}
            title={!customerPhone ? 'No phone on file' : 'Text customer'}
          >
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Text
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No messages yet. Email or text {customerName.split(' ')[0]} to start the thread — replies land in your real inbox/phone and sync here automatically.
          </p>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {messages.map(m => {
              const isOut = m.direction === 'out';
              const ChannelIcon = m.channel === 'email' ? Mail : MessageCircle;
              const DirIcon = isOut ? Send : Inbox;
              const ts = m.received_at || m.sent_at || m.created_at;
              const failed = m.status === 'failed' || !!m.error;
              return (
                <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg border px-3 py-2 ${
                    isOut
                      ? 'bg-primary/10 border-primary/30 text-foreground'
                      : 'bg-muted/40 border-border'
                  } ${failed ? 'border-red-500/50 bg-red-500/5' : ''}`}>
                    <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      <ChannelIcon className="h-3 w-3" />
                      <DirIcon className="h-3 w-3" />
                      <span>{isOut ? 'You' : customerName.split(' ')[0]}</span>
                      <span>·</span>
                      <span>{fmtTime(ts)}</span>
                      {failed && <span className="text-red-400 font-semibold ml-1">FAILED</span>}
                    </div>
                    {m.subject && m.channel === 'email' && (
                      <p className="text-xs font-semibold mb-1 truncate">{m.subject}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{m.body_text || <span className="text-muted-foreground italic">(no content)</span>}</p>
                    {failed && m.error && (
                      <p className="text-[11px] text-red-400 mt-1">{m.error}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {composeChannel && (
        <ComposeMessageDialog
          open={!!composeChannel}
          onOpenChange={(o) => { if (!o) setComposeChannel(null); }}
          channel={composeChannel}
          dealId={dealId}
          dealershipId={dealershipId}
          customerName={customerName}
          to={composeChannel === 'email' ? (customerEmail ?? '') : (customerPhone ?? '')}
          onSent={() => { setComposeChannel(null); load(); }}
        />
      )}
    </Card>
  );
}